import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  Heart,
  Crown,
  Loader2,
  Check,
  X,
  Sparkles,
  Star,
} from "lucide-react";
import { toast } from "sonner";

const zodiacSigns = [
  { name: "Aries", symbol: "♈", dates: "Mar 21 - Apr 19", element: "Fire" },
  { name: "Taurus", symbol: "♉", dates: "Apr 20 - May 20", element: "Earth" },
  { name: "Gemini", symbol: "♊", dates: "May 21 - Jun 20", element: "Air" },
  { name: "Cancer", symbol: "♋", dates: "Jun 21 - Jul 22", element: "Water" },
  { name: "Leo", symbol: "♌", dates: "Jul 23 - Aug 22", element: "Fire" },
  { name: "Virgo", symbol: "♍", dates: "Aug 23 - Sep 22", element: "Earth" },
  { name: "Libra", symbol: "♎", dates: "Sep 23 - Oct 22", element: "Air" },
  { name: "Scorpio", symbol: "♏", dates: "Oct 23 - Nov 21", element: "Water" },
  { name: "Sagittarius", symbol: "♐", dates: "Nov 22 - Dec 21", element: "Fire" },
  { name: "Capricorn", symbol: "♑", dates: "Dec 22 - Jan 19", element: "Earth" },
  { name: "Aquarius", symbol: "♒", dates: "Jan 20 - Feb 18", element: "Air" },
  { name: "Pisces", symbol: "♓", dates: "Feb 19 - Mar 20", element: "Water" },
];

const elementColors = {
  Fire: "from-orange-500/20 to-red-500/20 border-orange-500/30",
  Earth: "from-green-500/20 to-emerald-500/20 border-green-500/30",
  Air: "from-blue-500/20 to-cyan-500/20 border-blue-500/30",
  Water: "from-indigo-500/20 to-purple-500/20 border-purple-500/30",
};

const CompatibilityPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedSign, setSelectedSign] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCompatibility = async (sign) => {
    setLoading(true);
    setSelectedSign(sign);
    try {
      const response = await api.get(`/premium/compatibility/${sign}`);
      setCompatibility(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Premium subscription required");
        navigate("/premium");
      } else if (error.response?.status === 400) {
        toast.error("Please complete your profile with date of birth");
        navigate("/profile");
      } else {
        console.error("Error fetching compatibility:", error);
        toast.error("Failed to load compatibility");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="compatibility-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/premium")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-display text-xl text-white">Compatibility</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] font-body">Premium</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Title */}
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 className="font-display text-2xl sm:text-3xl text-white mb-2">Star Sign Compatibility</h1>
          <p className="text-white/60 font-body">
            {compatibility 
              ? `Your sign: ${compatibility.user_sign} • Partner: ${compatibility.partner_sign}`
              : "Select a sign to check your cosmic compatibility"}
          </p>
        </div>

        {/* Sign Selector */}
        <ScrollArea className="mb-8">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            {zodiacSigns.map((sign) => (
              <button
                key={sign.name}
                onClick={() => fetchCompatibility(sign.name)}
                disabled={loading}
                className={`p-3 sm:p-4 rounded-xl border transition-all duration-300 ${
                  selectedSign === sign.name
                    ? `bg-gradient-to-br ${elementColors[sign.element]} border-[#D4AF37]`
                    : "border-white/10 bg-white/5 hover:bg-white/10"
                }`}
              >
                <span className="text-2xl sm:text-3xl block mb-1">{sign.symbol}</span>
                <span className="text-xs sm:text-sm text-white font-body">{sign.name}</span>
              </button>
            ))}
          </div>
        </ScrollArea>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <p className="text-white/70 font-body">Analyzing cosmic alignment...</p>
          </div>
        )}

        {/* Compatibility Results */}
        {compatibility && !loading && (
          <div className="space-y-6 animate-fade-in-up">
            {/* Overall Score */}
            <div className="gold-accent-card p-6 sm:p-8 text-center">
              <div className="flex items-center justify-center gap-6 mb-4">
                <div className="text-center">
                  <span className="text-4xl block mb-1">
                    {zodiacSigns.find(s => s.name === compatibility.user_sign)?.symbol}
                  </span>
                  <span className="text-white/70 font-body text-sm">{compatibility.user_sign}</span>
                </div>
                <Heart className="w-8 h-8 text-pink-400 animate-pulse" />
                <div className="text-center">
                  <span className="text-4xl block mb-1">
                    {zodiacSigns.find(s => s.name === compatibility.partner_sign)?.symbol}
                  </span>
                  <span className="text-white/70 font-body text-sm">{compatibility.partner_sign}</span>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 mb-4">
                <Star className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-display text-2xl text-[#D4AF37]">{compatibility.overall_compatibility}</span>
              </div>
              <p className="text-white/80 font-body">{compatibility.summary}</p>
            </div>

            {/* Strengths & Challenges */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="glass-card p-6">
                <h3 className="font-display text-lg text-white mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-400" /> Strengths
                </h3>
                <ul className="space-y-2">
                  {compatibility.strengths.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glass-card p-6">
                <h3 className="font-display text-lg text-white mb-4 flex items-center gap-2">
                  <X className="w-5 h-5 text-orange-400" /> Challenges
                </h3>
                <ul className="space-y-2">
                  {compatibility.challenges.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Love Potential */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-pink-400" />
                </div>
                <h3 className="font-display text-lg text-white">Love Potential</h3>
              </div>
              <p className="text-white/70 font-body leading-relaxed">{compatibility.love_potential}</p>
            </div>

            {/* Communication */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#C4B5FD]" />
                </div>
                <h3 className="font-display text-lg text-white">Communication Style</h3>
              </div>
              <p className="text-white/70 font-body leading-relaxed">{compatibility.communication_style}</p>
            </div>

            {/* Advice */}
            <div className="gold-accent-card p-6">
              <h3 className="font-display text-xl text-white mb-3">Making It Work</h3>
              <p className="text-white/80 font-body leading-relaxed">{compatibility.advice}</p>
            </div>

            {/* Try Another */}
            <div className="text-center pt-4">
              <Button
                onClick={() => {
                  setCompatibility(null);
                  setSelectedSign(null);
                }}
                className="btn-secondary"
              >
                Check Another Sign
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompatibilityPage;
